import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    CHART_COLORS,
    CHART_CONST_INFO,
    CHART_TYPE,
    REPORT_TYPE,
    SEGMENT_BY,
} from '@constants/common/chart/chartConst';
import { formatDate } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { configs } from '../../constants/configs/configs';
import BarChartContainer from './BarChartContainer';
import UpcomingDueDate from './UpcomingDueDate';
import useChartInfo from './useChartInfo';

function UpcomingDueDateContainer() {
    const [serviceResponse, setServiceResponse] = useState<any[]>([]);
    const [defaultReportCoolers, setDefaultReportCoolers] = useState<string[]>(CHART_COLORS.STATUS_REPORTS_COLOR);
    const [showAssigneeSegment, setshowAssigneeSegment] = useState(false);
    const [selectedReport, setSelectedReport] = useState<string>(REPORT_TYPE.UPCOMING_DUE_DATES);
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [selectedChart, setselectedChart] = useState<string>(CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS);
    const [chartData, setchartData] = useState<any[]>([]);
    const [idMapping, setidMapping] = useState<{}[]>([]);
    const [selectedWorkGropeName, setSelectedWorkGropeName] = useState<string>('');
    const [selectedId, setSelectedBarId] = useState<number>();
    const [chartColors, setChartColors] = useState<string[]>(CHART_COLORS.STATUS_REPORTS_COLOR);
    const [isLegendFilterApplied, setIsLegendFilterApplied] = useState<string | null>(null);
    const [isWorkGroupFilterApplied, setIsWorkGroupFilterApplied] = useState<boolean>(false);
    const [legendClassName, setLegendClassName] = useState<string[]>(['na']);
    const [isDrillDownActive, setIsDrillDownActive] = useState<boolean>(false);
    const [isDrillDownAllowed, setIsDrillDownAllowed] = useState<boolean>(true);
    const [segmentBy, setSegmentBy] = useState<string>(SEGMENT_BY.TAB_STATUS);
    const [workGroupFilter, setworkGroupFilter] = useState<any>(null);
    const [isTagFilterApplied, setIsTagFilterApplied] = useState<boolean>(true);
    const [showBy, setShowBy] = useState<string>(SEGMENT_BY.WORKGROUP);
    const [drillDownData, setDrillDownData] = useState<any>([]);

    const {
        chartInfoData,
        fetchDueDateByWorkGroup,
        startSpinner,
        stopSpinner,
        fetchResolvedDateReportsByWorkGroup,
        fetchResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown,
        getResolvedDateReportsGroupByAssignee,
        getResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown,
        getDueDateReportsByWorkGroupTags,
        getDueDateReportsByAssineeTags,
        getOpenTicketReportsByTags,
    } = useChartInfo();

    const getChartColorsInfo = () => {
        if (selectedReport !== REPORT_TYPE.UPCOMING_DUE_DATES) {
            return CHART_COLORS.RESOLVED_DATE_REPORTS_COLOR;
        }
        return segmentBy === SEGMENT_BY.TAB_STATUS
            ? CHART_COLORS.STATUS_REPORTS_COLOR
            : CHART_COLORS.DUEDATE_DATE_REPORTS_COLOR;
    };

    useEffect(() => {
        if (serviceResponse.length > 0) {
            multipleFilterApplied(isLegendFilterApplied, workGroupFilter);
        }
    }, [serviceResponse, isLegendFilterApplied, workGroupFilter]);

    useEffect(() => {
        const chartColorsInfo: string[] = getChartColorsInfo();

        if (isLegendFilterApplied && chartData.length > 0) {
            const selectedIndex = serviceResponse[0]?.indexOf(chartData[0][1]);
            switch (segmentBy) {
                case SEGMENT_BY.TAB_STATUS: {
                    setChartColors([CHART_COLORS.STATUS_REPORTS_COLOR[selectedIndex - 1]]);
                    break;
                }
                case SEGMENT_BY.TAB_DUE_DATE: {
                    setChartColors([CHART_COLORS.DUEDATE_DATE_REPORTS_COLOR[selectedIndex - 1]]);
                    break;
                }
                case SEGMENT_BY.ASSIGNEE:
                case SEGMENT_BY.WORKGROUP: {
                    setChartColors([CHART_COLORS.RESOLVED_DATE_REPORTS_COLOR[selectedIndex - 1]]);
                    break;
                }
                default:
                    setChartColors(chartColorsInfo);
                    setDefaultReportCoolers(chartColorsInfo);
                    break;
            }
        } else {
            setChartColors(chartColorsInfo);
            setDefaultReportCoolers(chartColorsInfo);
        }
    }, [chartData]);

    const changeReport = value => {
        clearFilters();
        setchartData([]);
        setServiceResponse([]);
        setSelectedReport(value);
        setIsDrillDownActive(false);
        setIsDrillDownAllowed(true);
        if (value === REPORT_TYPE.UPCOMING_DUE_DATES) {
            setshowAssigneeSegment(false);
            setSegmentBy(SEGMENT_BY.TAB_STATUS);
        } else {
            const activeTabInfo = SEGMENT_BY.WORKGROUP;
            setSegmentBy(activeTabInfo);
        }
    };

    const getWorkNameOrAssigneeName = workGroupFilter => {
        let filterItem: any = '';
        Object.entries(idMapping).forEach((value): void => {
            Object.entries(value[1]).forEach((item): void => {
                if (item[0] === workGroupFilter) {
                    filterItem = item[1];
                }
            });
        });
        return filterItem;
    };

    const clearFilters = () => {
        setIsLegendFilterApplied(null);
        setIsWorkGroupFilterApplied(false);
        setworkGroupFilter(null);
        setSelectedBarId(undefined);
        setDrillDownData([]);
        setSelectedWorkGropeName('');
    };

    const applyLegendFilter = legendFilter => {
        const legendFilterData: any = [];
        legendFilterData.push([serviceResponse[0][0], legendFilter]);
        const selectedIndex = serviceResponse[0].indexOf(legendFilter);
        serviceResponse.forEach((rowData, serverIndex) => {
            if (serverIndex > 0) {
                rowData.forEach((element, index) => {
                    if (index === selectedIndex) {
                        legendFilterData.push([rowData[0], element]);
                    }
                });
            }
        });
        return legendFilterData;
    };

    const applyWorkgroupFilter = (workGroupFilter, filterList = serviceResponse) => {
        const filterName = getWorkNameOrAssigneeName(workGroupFilter);
        const workgroupFilteredChartData: any = [];
        workgroupFilteredChartData.push(filterList[0]);
        const data: any = filterList?.filter((value, index) => index > 0 && value[0] === filterName);
        workgroupFilteredChartData.push(...data);
        return workgroupFilteredChartData;
    };

    const multipleFilterApplied = (legendFilter, workGroupFilter) => {
        const legendFilteredData = legendFilter ? applyLegendFilter(legendFilter) : serviceResponse;
        const workgroupFilteredData = workGroupFilter
            ? applyWorkgroupFilter(workGroupFilter, legendFilteredData)
            : legendFilteredData;
        setchartData(workgroupFilteredData);
    };

    const filterBasedOnLegend = (e, legendFilter) => {
        const indexId = e.target.id;
        if (legendClassName[indexId] === '--clickable') {
            setIsLegendFilterApplied(legendFilter);
            const legendClassNameUpdated = legendClassName.map((_item, index) =>
                Number(index) === Number(indexId) ? '--clicked' : '--clickable',
            );
            setLegendClassName(legendClassNameUpdated);
            if (isWorkGroupFilterApplied) {
                multipleFilterApplied(legendFilter, workGroupFilter);
            } else {
                const legendFilterData = applyLegendFilter(legendFilter);
                setchartData(legendFilterData);
            }
        } else {
            legendClassName[indexId] = '--clickable';
            setLegendClassName(legendClassName);
            setIsLegendFilterApplied(null);
            multipleFilterApplied('', workGroupFilter);
        }
    };

    const filterBySelectedWorkGroup = workGroupFilter => {
        if (workGroupFilter === null || workGroupFilter === '') {
            setworkGroupFilter(null);
            setchartData(serviceResponse);
        } else {
            setworkGroupFilter(workGroupFilter);
            setIsWorkGroupFilterApplied(true);
        }
    };

    const onSelectedDate = date => {
        setworkGroupFilter(null);
        setIsLegendFilterApplied(null);
        setSelectedDate(date);
        fetchDataFromApi(selectedChart, selectedId, date);
    };

    const loadChartData = response => {
        stopSpinner();
        const formattedData: any[] = [];
        const idMapping = response?.responseBody?.idMapping;
        const category = response?.responseBody?.category;
        const responseData = response?.responseBody?.responseData;
        if (responseData) {
            formattedData.push(category);
            responseData.forEach((data: {}) => {
                const responseArray: any[] = [];
                idMapping.forEach((item: object) => {
                    const value = item[Number(Object.keys(data)[0])];
                    if (value) {
                        responseArray.push(value);
                    }
                });
                responseArray.push(...data[Object.keys(data)[0]]);
                formattedData.push(responseArray);
            });
            const numberOfLegendClass = formattedData
                ?.filter((v, i) => i === 0)[0]
                .filter((v, i) => i > 0)
                .map(v => '--clickable');
            setLegendClassName(numberOfLegendClass);
            setServiceResponse(formattedData);
            setidMapping(idMapping);
        }

        if (selectedReport && selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES) {
            setSelectedDate(selectedDate =>
                response?.resultDate ? new Date(response?.resultDate) : selectedDate ? selectedDate : new Date(),
            );
        }
    };

    const fetchDataFromApi = (
        selectedChart: string,
        selectedId?: number | null,
        selectedDate?: any,
        selectedReport: any = REPORT_TYPE.UPCOMING_DUE_DATES,
    ) => {
        startSpinner();
        setchartData([]);
        setServiceResponse([]);
        let payload = {};
        let actionCall;

        switch (selectedChart) {
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_STATUS:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE:
            case CHART_TYPE.ACTIVATE_STATUS_BY_USERS:
            case CHART_TYPE.ACTIVATE_DUEDATE_BY_USERS:
                payload = {
                    activeChart: selectedChart || '',
                    workgroup: selectedId || '',
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                };
                actionCall = fetchDueDateByWorkGroup;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN_USER:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN_USER:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS_DRILLDOWN:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE_DRILLDOWN:
                payload = {
                    activeChart: selectedChart || '',
                    userId: selectedId || '',
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                };
                actionCall = fetchDueDateByWorkGroup;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS_DRILL_DOWN:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                };
                actionCall = getDueDateReportsByWorkGroupTags;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                };
                actionCall = getDueDateReportsByAssineeTags;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                    tagSwitchOn: isTagFilterApplied,
                };
                actionCall = getOpenTicketReportsByTags;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE_DRILL_DOWN:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                };
                actionCall = getOpenTicketReportsByTags;
                break;

            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, workGroupId: selectedId };
                actionCall = fetchResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown;
                break;
            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, assignee: selectedId };
                actionCall = getResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown;
                break;
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_TAG_DUEDATE_RESOLVED_TODAY:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_TAG_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, tagId: selectedId };
                actionCall = fetchResolvedDateReportsByWorkGroup;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_TAG_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, tagId: selectedId };
                actionCall = getResolvedDateReportsGroupByAssignee;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE:
            case CHART_TYPE.RESOLVED_DATE_REPORTS_TAG_GROUP_BY_ASSIGNEE:
                payload = { tagSwitchOn: isTagFilterApplied, tagId: selectedId };
                actionCall = getResolvedDateReportsGroupByAssignee;
                break;
            default:
                break;
        }
        setidMapping([]);
        actionCall(payload).then(response => {
            setshowAssigneeSegment(response?.showAssigneeSegment);
            loadChartData(response);
        });
    };

    const backToDetailedReport = useCallback(() => {
        const {  selectedChart, selectedWorkgroup = null } = drillDownData.pop();
        const lastDrillDownData = drillDownData?.[drillDownData.length - 1];
        setIsDrillDownActive(drillDownData.length > 0);
        setIsDrillDownAllowed(true);
        setSelectedWorkGropeName(lastDrillDownData?.selectedWorkGropeName || '');
        setSelectedBarId(lastDrillDownData?.selectedId ?? undefined);
        setselectedChart(selectedChart);
        setworkGroupFilter(selectedWorkgroup);
        setIsLegendFilterApplied(null);
    }, [drillDownData]);

    const getFilter = parts => {
        const isTodayActivityReport = REPORT_TYPE.UPCOMING_DUE_DATES !== selectedReport;
        const isDueDateReport = SEGMENT_BY.TAB_DUE_DATE === segmentBy;

        if (isLegendFilterApplied) {
            const filterIndex = legendClassName.map(v => v === '--clicked').indexOf(true);
            if (isTodayActivityReport) {
                return CHART_CONST_INFO.TODAYS_ACTIVITY_LIST[filterIndex];
            } else if (isDueDateReport) {
                return CHART_CONST_INFO.DUEDATE_FILTER_LIST[filterIndex];
            } else {
                return CHART_CONST_INFO.STATUS_FILTER_LIST[filterIndex];
            }
        }
        if (isTodayActivityReport) {
            return CHART_CONST_INFO.TODAYS_ACTIVITY_LIST[parts[1]];
        } else if (isDueDateReport) {
            return CHART_CONST_INFO.DUEDATE_FILTER_LIST[parts[1]];
        } else {
            return CHART_CONST_INFO.STATUS_FILTER_LIST[parts[1]];
        }
    };

    const getSelectedBarId = (parts: string[]): number | null => {
        const selectedBarIndex = Number(parts[2]);
        if (selectedBarIndex >= 0 && selectedBarIndex < idMapping.length) {
            const selectedGroup = idMapping[selectedBarIndex] || {};
            return Number(Object.keys(selectedGroup)[0]);
        }
        return null;
    };

    const toUserChart = e => {
        const isResolvedTodayReport: boolean = selectedReport !== REPORT_TYPE.UPCOMING_DUE_DATES;
        const parts = e.targetID.split('#');
        const wokGroupDueDateReport =
            selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES
                ? showBy === SEGMENT_BY.WORKGROUP
                : segmentBy === SEGMENT_BY.WORKGROUP;
        if (parts[0] === 'bar') {
            const ticketListSortFromChart = {
                dueReport: segmentBy === SEGMENT_BY.TAB_DUE_DATE ? 'yes' : 'no',
                pageNumber: 1,
                pageSize: 10,
                sortOrder: 'DESC',
                sortBy: 'assignee',
                workgroupId:
                    getTitle === SEGMENT_BY.WORKGROUP ? getSelectedBarId(parts) : getParentId(drillDownData, true),
                filter: getFilter(parts),
                assigneeId:
                    getTitle === SEGMENT_BY.USER || getTitle === SEGMENT_BY.ASSIGNEE
                        ? getSelectedBarId(parts)
                        : getParentId(drillDownData, false),
                tagId: getTitle === SEGMENT_BY.TAG ? getSelectedBarId(parts) : '',
                dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                resolvedReport: isResolvedTodayReport ? true : false,
                wokGroupDueDateReport,
            };

            startSpinner();
            sessionStorage.setItem('ticketListSortFromChart', JSON.stringify(ticketListSortFromChart));
            window.location.href = configs.endpoints.TICKET_MGMT_DUE_DATE_REPOST;
        }
        if (isDrillDownAllowed && parts[0] === 'hAxis') {
            clearFilters();

            if (parts.indexOf('label') >= 0) {
                let idx = parts[parts.indexOf('label') + 1];
                idx = parseInt(idx);
                setSelectedWorkGropeName(chartData[idx + 1][0]);
                const selectedId: number = Number(
                    Object.keys(idMapping?.filter((acc: {}) => Object.values(acc)[0] === chartData[idx + 1][0])[0])[0],
                );
                setDrillDownChart(selectedId);
                setIsDrillDownActive(true);
                setDrillDownData([
                    ...drillDownData,
                    {
                        selectedId,
                        selectedWorkGropeName: chartData[idx + 1][0],
                        selectedChart,
                        selectedWorkgroup: workGroupFilter,
                        type: getTitle,
                    },
                ]);
            }
        }
    };

    const setDrillDownChart = id => {
        setSelectedBarId(id);
        startSpinner();

        let newChart = selectedChart;
        let drilldownAllowed = false;

        switch (selectedChart) {
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS_DRILLDOWN;
                drilldownAllowed = false;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE_DRILLDOWN;
                drilldownAllowed = false;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN;
                drilldownAllowed = true;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN;
                drilldownAllowed = true;
                break;
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE_DRILL_DOWN:
                newChart = CHART_TYPE.ACTIVATE_DUEDATE_BY_USERS;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN_USER;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN_USER;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS_DRILL_DOWN:
                newChart = CHART_TYPE.ACTIVATE_STATUS_BY_USERS;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_TAG_DUEDATE_RESOLVED_TODAY:
                newChart = CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_TAG_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_TAG_GROUP_BY_ASSIGNEE:
                newChart = CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_TAG_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE:
            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_TAG_DRILL_DOWN:
                newChart = CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_DRILL_DOWN;
                drilldownAllowed = false;
                break;
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_TAG_DRILL_DOWN:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY:
                newChart = CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_DRILL_DOWN;
                drilldownAllowed = false;
                break;

            default:
                break;
        }
        setselectedChart(newChart);
        setIsDrillDownAllowed(drilldownAllowed);
    };

    useEffect(() => {
        const determineChartType = () => {
            if (selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES) {
                if (isTagFilterApplied) {
                    if (showBy === SEGMENT_BY.WORKGROUP) {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE
                            : CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS;
                    } else {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE
                            : CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS;
                    }
                } else {
                    if (showBy === SEGMENT_BY.WORKGROUP) {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE
                            : CHART_TYPE.ACTIVATE_WORKGROUP_BY_STATUS;
                    } else {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE
                            : CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS;
                    }
                }
            } else {
                if (isTagFilterApplied) {
                    return segmentBy === SEGMENT_BY.WORKGROUP
                        ? CHART_TYPE.ACTIVATE_WORKGROUP_BY_TAG_DUEDATE_RESOLVED_TODAY
                        : CHART_TYPE.RESOLVED_DATE_REPORTS_TAG_GROUP_BY_ASSIGNEE;
                } else {
                    return segmentBy === SEGMENT_BY.WORKGROUP
                        ? CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY
                        : CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE;
                }
            }
        };

        setselectedChart(determineChartType());
        setworkGroupFilter(null);
        setIsLegendFilterApplied(null);
    }, [isTagFilterApplied, segmentBy, showBy]);

    useEffect(() => {
        fetchDataFromApi(selectedChart, selectedId, selectedDate);
    }, [selectedChart]);

    const onTagFilterToggle = isEnabled => {
        setIsDrillDownAllowed(true);
        setIsTagFilterApplied(isEnabled);
        setDrillDownData([]);
        if (!isEnabled) {
            if (selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES) {
                setShowBy(SEGMENT_BY.WORKGROUP);
            } else {
                setSegmentBy(SEGMENT_BY.WORKGROUP);
            }
        }
    };

    const getTitle = useMemo(() => {
        const title = chartData?.[0]?.[0] || '';
        return String(title).charAt(0).toUpperCase() + String(title).slice(1);
    }, [chartData]);

    const getParentId = useCallback((drillDownData, getWorkGroupId) => {
        const workgroupData = drillDownData.find((drillData: { type: string; selectedId: number }) => {
            if (getWorkGroupId) {
                return drillData.type === SEGMENT_BY.WORKGROUP;
            } else {
                return drillData.type === SEGMENT_BY.ASSIGNEE || drillData.type === SEGMENT_BY.USER;
            }
        });
        return workgroupData?.selectedId || '';
    }, []);

    return (
        <>
            <UpcomingDueDate
                segmentBy={segmentBy}
                setSegmentBy={setSegmentBy}
                selectedReport={selectedReport}
                RenderByWorkGroup={
                    <BarChartContainer
                        selectedReport={selectedReport}
                        selectedDate={selectedDate}
                        onSelectedDate={onSelectedDate}
                        isDrillDownActive={isDrillDownActive}
                        backToDetailedReport={backToDetailedReport}
                        selectedWorkGropeName={selectedWorkGropeName}
                        chartData={chartData}
                        selectedChart={selectedChart}
                        chartColors={chartColors}
                        toUserChart={toUserChart}
                        serviceResponse={serviceResponse}
                        legendClassName={legendClassName}
                        filterBasedOnLegend={filterBasedOnLegend}
                        defaultReportCoolers={defaultReportCoolers}
                        isDrillDownAllowed={isDrillDownAllowed}
                        isLoading={chartInfoData.isSpinning}
                        title={getTitle}
                    />
                }
                changeReport={changeReport}
                showAssigneeSegment={showAssigneeSegment}
                idMapping={idMapping}
                filterBySelectedWorkGroup={filterBySelectedWorkGroup}
                isDrillDownActive={isDrillDownActive}
                isTagFilterApplied={isTagFilterApplied}
                setIsTagFilterApplied={onTagFilterToggle}
                workGroupFilter={workGroupFilter}
                selectedChart={selectedChart}
                showBy={showBy}
                setShowBy={setShowBy}
                title={getTitle}
            />
        </>
    );
}
export default UpcomingDueDateContainer;
